import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { isNetworkShapedError, isOfflineShapedError } from '../lib/api';
import { IS_APP_BUILD } from '../lib/buildTarget';
import { useAuth } from '../contexts/AuthContext';
import { ArrowLeft, History, RotateCw } from 'lucide-react';
import { toast } from 'sonner';
import { useReadyTranslation } from '../hooks/useReadyTranslation';
import { useScreenData } from '../hooks/useScreenData';
import { useNetworkStatus } from '../hooks/useNetworkStatus';
import { invalidateScreenData } from '../lib/screenDataCache';
import { SCREEN_CORRECTED_EVENT } from '../lib/screenEvents';
import { isExplicitlyOffline } from '../lib/offline/networkVerdict';
import { log as diagLog } from '../lib/diag/readyChainLog';
import { Boton, EncabezadoDePagina, RecuadroDeEstado, avisarSinRed } from '../components/alumno';
import RenglonDeSesion, { EsqueletoDelHistorial } from '../components/historial/RenglonDeSesion';
import { getLocalizedMode } from '../lib/i18nHelpers';
import { formatearFechaDeSesion } from '../lib/fechaDeSesion';
import { useReacomodo, reducirMovimiento, REACOMODO_MS } from '../components/panel/useReacomodo';

// pantallas-alumno (F): la pagina del Historial, hasta 1040 y centrada (4.1 del encargo). El icono y el
// color de cada modo viven en `components/historial/RenglonDeSesion.js` (los tres con el mismo naranja).
const PAGINA = 'mx-auto w-full max-w-[1040px] flex flex-col gap-5 md:gap-6';

// smoke-alumno (S10): la celda de cada sesion, la que mide el reacomodo (`data-celda`). Al despedirse baja a
// 28 % y se encoge a 97 % en 200 ms, los mismos numeros de la salida del Panel (A15); con "reducir movimiento",
// sin transicion.
export const OPACIDAD_SALIENDO = 0.28;
export const ESCALA_SALIENDO = 0.97;

function CeldaQueSeDespide({ id, saliendo, children }) {
  const quieto = reducirMovimiento();
  return (
    <div
      data-celda={id}
      style={{
        opacity: saliendo ? OPACIDAD_SALIENDO : 1,
        transform: saliendo ? `scale(${ESCALA_SALIENDO})` : undefined,
        transition: quieto ? undefined : `opacity ${REACOMODO_MS}ms ease-out, transform ${REACOMODO_MS}ms ease-out`,
      }}
    >
      {children}
    </div>
  );
}

// P1 de la parte 2: la sesion que no se pudo borrar vuelve a su lugar (por fecha de inicio, de la mas
// nueva a la mas vieja, el orden de la lista). Si ya esta (la recarga gano), no se toca nada.
export function regresarSesion(lista, sesion) {
  const actual = Array.isArray(lista) ? lista : [];
  if (!sesion || actual.some((s) => s && s.id === sesion.id)) return actual;
  const llave = String(sesion.started_at || '');
  const j = actual.findIndex((s) => String((s && s.started_at) || '') < llave);
  if (j === -1) return [...actual, sesion];
  return [...actual.slice(0, j), sesion, ...actual.slice(j)];
}

// Translates backend mode strings to localized labels (reusable pattern for later batches)
// remate ajustes-1-3-0 (R2, #509): la fecha en el idioma del alumno (es -> es-MX, en -> en-US),
// dateStyle medium + timeStyle short, sin la palabra inglesa entre fecha y hora; la hora sigue
// siendo la LOCAL del aparato (el instante viene con +00:00). Decia fecha en-US, la palabra
// inglesa y hora en-US, tambien en espanol.
function formatDate(iso, idioma) {
  return formatearFechaDeSesion(iso, idioma);
}

// Fetcher del historial — cacheado bajo 'study-log' (regla de frescura F4.3b;
// al salir de una sesión de estudio App.js invalida esta key).
// F6.4b: fetch vivo caído con forma-de-red en nativo → el MISMO shape (array
// de sesiones) sale de la bodega por el camino normal del fetcher.
// RPW T9 (P2) — UNA FOTO CON EXÁMENES VIVOS CADUCA DE INMEDIATO.
//
// EL HALLAZGO (autopsia P0 de la T9, medido en producción): el backend puede
// tener razón y la pantalla mentir igual. El cache de pantalla guarda el último
// dato bueno 30 s y dentro de esa ventana NO re-pide (useScreenData:53). Una
// mirada al Study Log que cae mientras el examen todavía corre pinta "en curso"
// —correcto en ese instante— y ese renglón se queda pintado hasta 30 s después
// de que el reloj llegó a cero. Sumado a los 60 s de gracia que la P3 mata, eso
// daba los 90 s exactos que Carlos midió en el re-test.
//
// El mínimo cambio que lo cierra: si el payload trae UN examen no-final, la
// entrada de cache se invalida. Lo pintado no cambia (nada de stale-then-swap:
// el usuario sigue viendo lo que el servidor acaba de decir), pero la SIGUIENTE
// entrada a la pantalla vuelve al servidor en vez de reusar una foto que ya
// puede haber caducado. Cero polling nuevo, cero peticiones extra por visita.
function tieneExamenNoFinal(sessions) {
  return (sessions || []).some(
    (s) => s.mode === 'exam' && s.status !== 'completed' && s.status !== 'abandoned');
}

// ── R53 (C-F1) — LA APP FUSIONA SIEMPRE, CON SEÑAL O SIN ELLA ───────────────
//
// EL HALLAZGO (censo R53 §C). Con señal, `fetchStudyLogSessions` JAMÁS consultaba
// la bodega: la fusión local —la que sí trae las actas sin ackear, los en-vuelo
// nacidos en el teléfono y las abandonadas— sólo corría con dictamen offline o
// con error de forma-de-red. La verdad estaba a un `import()` de distancia y el
// camino feliz no la miraba.
//
// Y LA ENTRADA EN FRÍO NO ES UN CASO RARO: ES EL CASO. `App.js:148-155` invalida
// `study-log` al SALIR de una ruta de estudio, así que la entrada de después de
// entregar es SIEMPRE en frío: se va al servidor, el servidor todavía no conoce
// el acta (el cartero va detrás), se pinta sin la entrega, y esa respuesta se
// re-cachea 30 s. El write-through de la S-2 no lo salva y no puede: para un
// examen NACIDO EN EL CEL la fila no está en el payload del servidor, así que su
// parche degrada siempre a "invalidar".
//
// POR QUÉ ESTA SUPERFICIE Y NO EL CACHE (opción (c) del censo, C-3): la
// maquinaria ya existe, ya es el contrato de esta pantalla y ya está probada
// (`studyLogFromWarehouse` + `StudyLog.fusionLocal.test.js`). Lo NUEVO es
// correrla en el camino feliz. Ataca la causa —la app no mira su propia bodega—
// en vez del síntoma —el cache no recuerda—, y hace innecesarias las opciones (a)
// y (b), que exigían una segunda verdad por fila con su regla de expulsión.
//
// LO QUE ESTE CIERRE **NO** CUBRE, y queda anotado: el mismo síntoma existe en
// `study-center` y `analytics-overview`, que sólo reciben invalidación
// (`screenDataCache.js:184`). No se tocan aquí.
const STUDYLOG_FUSION_ONLINE = true;
let studyLogFusionOnline = STUDYLOG_FUSION_ONLINE;

/** Solo para tests: apagar/encender la fusión con señal y medir la bit-identidad. */
export function __setStudyLogFusionOnlineForTests(on) {
  studyLogFusionOnline = on === undefined ? STUDYLOG_FUSION_ONLINE : !!on;
}

// LA REGLA DE IDENTIDAD, entera: la bodega APORTA lo que el servidor no trae, y
// ante la misma sesión en las dos GANA LA VERSIÓN MÁS FINAL — a igual finalidad,
// manda el servidor. Sin aporte y sin nada más final se devuelve el arreglo del
// servidor TAL CUAL (misma referencia), así que el camino feliz de hoy queda
// idéntico cuando no hay nada que sumar ni que corregir.
//
// R53-b (pieza 1) — POR QUÉ LA FINALIDAD Y NO "EL SERVIDOR SIEMPRE". El hallazgo
// de Carlos, con su mecanismo: el cascarón que la tanda C dejó vivo en el
// servidor como `in_progress` hace que un acta local ENTREGADA pierda contra su
// PROPIO cascarón rancio. La sesión SÍ está en el payload del servidor —así que
// el aporte por membresía no la alcanza— y "misma sesión → manda el servidor"
// pintaba "en progreso" encima de una entrega que este teléfono ya firmó. Se
// corregía sola cuando el cartero subía el acta: la ventana es EXACTAMENTE el lag
// del cartero, y durante ella la pantalla miente.
//
// EL CRITERIO DE FINALIDAD NO ES NUEVO: es `localSupersedesPhoto`, la regla G-3
// del contrato de fusión offline (`brain/analyticsReads.js:160-163`), que
// responde esta misma pregunta —cuál de los dos hechos es más nuevo— para el
// examen adoptado que se entrega sin señal. Reusarla es lo que impide que la app
// tenga DOS definiciones de "terminado": lo final es `completed`, el HECHO, y en
// cuanto el servidor traiga la sesión completada vuelve a mandar él —que es quien
// firma la calificación— y el número no brinca.
//
// El import es DINÁMICO, como el de la bodega, y no cuesta nada: para llegar aquí
// `studyLogFromWarehouse` ya cargó ese mismo módulo por `brainReads()`
// (`screenSources.js:91`). La web no pide el chunk jamás.
//
// El orden es el de las dos fuentes —`started_at` desc, el del server y el que
// `readHistory` replica— así que la fila que aporta la bodega cae donde le toca
// por fecha y no al final de la lista. Una fila PISADA conserva su posición: es
// la misma sesión, con el mismo `started_at`.
//
// JAMÁS RETRASA EL PINTADO MÁS QUE ELLA MISMA: es una lectura local, sin red. Y
// si truena, se pinta lo del servidor y consta en el visor por la familia
// `SCR.FALLBACK` que esta pantalla ya usa.
async function fusionarConBodega(delServidor, userId) {
  try {
    const sources = await import('../lib/warehouse/screenSources');
    const deBodega = await sources.studyLogFromWarehouse(userId);
    if (!Array.isArray(deBodega) || deBodega.length === 0) return delServidor;
    const delServidorPorId = new Map();
    for (const s of delServidor) if (s && s.id) delServidorPorId.set(s.id, s);
    const aporte = [];
    const choques = [];
    for (const s of deBodega) {
      if (!s || !s.id) continue;
      const enServidor = delServidorPorId.get(s.id);
      if (enServidor === undefined) aporte.push(s);
      else choques.push([s, enServidor]);
    }
    // El clasificador sólo se carga si hay un duelo que resolver.
    const pisadas = new Map();
    if (choques.length > 0) {
      const reglas = await import('../lib/brain/analyticsReads');
      for (const [deBodegaFila, enServidor] of choques) {
        if (reglas.localSupersedesPhoto(deBodegaFila, enServidor)) {
          pisadas.set(deBodegaFila.id, deBodegaFila);
        }
      }
    }
    if (aporte.length === 0 && pisadas.size === 0) return delServidor;
    const base = pisadas.size === 0 ? delServidor
      : delServidor.map((s) => (s && s.id && pisadas.has(s.id) ? pisadas.get(s.id) : s));
    if (aporte.length === 0) return base;
    return [...base, ...aporte].sort(
      (a, b) => String(b?.started_at || '').localeCompare(String(a?.started_at || '')));
  } catch (e) {
    diagLog('SCR.FALLBACK', {
      pantalla: 'study-log', llave: 'study-log',
      motivo: 'fusion-en-linea', error: e?.message || String(e),
    });
    return delServidor;
  }
}

export async function fetchStudyLogSessions(userId) {
  // F6.7b (fase 2) — ATAJO OFFLINE: con DICTAMEN del vigía la petición está
  // condenada y sin timeout propio (api.js:18) el usuario espera al del
  // sistema. La bodega tiene el mismo shape y responde ya. Con red, camino
  // vivo idéntico al de hoy.
  //
  // R53: este atajo NO se toca. Sin señal ya se sirve la bodega ENTERA (que es
  // la fusión completa), así que fusionarla contra un servidor que no contestó
  // no añadiría una sola fila — y sí añadiría una segunda lectura.
  if (IS_APP_BUILD && isExplicitlyOffline()) {
    try {
      const sources = await import('../lib/warehouse/screenSources');
      const fromWarehouse = await sources.studyLogFromWarehouse(userId);
      if (fromWarehouse) return fromWarehouse;
    } catch (e) { /* sin bodega utilizable → camino normal */ }
  }
  try {
    const res = await api.get('/study/history');
    const delServidor = res.data.sessions || [];
    // R53 (C-F1) — EL CAMINO FELIZ TAMBIÉN MIRA LA BODEGA. Web: `IS_APP_BUILD`
    // es false y esta línea devuelve exactamente lo de hoy, cero fusión.
    if (!IS_APP_BUILD || !studyLogFusionOnline) return delServidor;
    return await fusionarConBodega(delServidor, userId);
  } catch (err) {
    if (IS_APP_BUILD && isNetworkShapedError(err)) {
      const sources = await import('../lib/warehouse/screenSources');
      const fromWarehouse = await sources.studyLogFromWarehouse(userId);
      if (fromWarehouse) return fromWarehouse;
    }
    throw err;
  }
}

export default function StudyLog() {
  const { t, ready, i18n } = useReadyTranslation(['study', 'common']);
  // R2: el idioma de i18n decide el locale de la fecha (sin i18n, en-US).
  const idiomaDeFecha = i18n && i18n.language;
  const navigate = useNavigate();
  const { user } = useAuth();
  // Identidad nueva por render sin costo: useScreenData guarda el fetcher en
  // un ref (F6.4b — el user.id viaja para la guardia de dueño del fallback).
  const { data, loading, error, setData, refetch } = useScreenData('study-log', () => fetchStudyLogSessions(user?.id));
  const sessions = data || [];
  const [deleting, setDeleting] = useState(null);
  // smoke-alumno (S10): la sesion que se esta despidiendo y la lista que el Panel mide para acomodar las demas.
  const [saliendo, setSaliendo] = useState(null);
  const listaRef = useRef(null);
  useReacomodo(listaRef, sessions.map((s) => s.id).join('|'));
  // F6.8 EH-1 — ESTA PANTALLA ENTRA AL REDIL. Era la única del censo que ni
  // importaba el hook de red: sin señal y sin bodega utilizable `data` quedaba
  // vacío y pintaba "aún no hay sesiones" — la lectura que no salió disfrazada de
  // historial vacío. Un solo consumo del veredicto EXISTENTE (mismo bus, mismo
  // oído: cero listeners nuevos) y el vacío vuelve a re-decidirse solo.
  const net = useNetworkStatus();
  // EH-2 — la MISMA frontera de Analíticas, con las mismas dos mitades: el
  // veredicto vivo, o un fallo que jamás llegó al servidor. Un 5xx NO entra aquí
  // (llegó con señal viva) y conserva el camino de hoy, toast incluido.
  const sinRed = net.isOffline || (error && isOfflineShapedError(error));

  // F6.8 R-1 — EL TOAST SE ALINEA CON EL DE ANALÍTICAS, condición idéntica a la
  // de `Analytics.js:301`. Un fallo con forma-de-red ya no suena: la tarjeta
  // honesta de abajo dice lo mismo y no se va sola. Un 5xx tampoco suena (para
  // `isNetworkShapedError` un 5xx y un 429 son forma-de-red), y ahí es el estado
  // 3 quien habla. Sigue sonando exactamente lo que suena allá: el error sin
  // forma de red, típicamente un 4xx, que además pinta el estado 3.
  //
  // F6.8 R-4 (firma de Carlos) — UNA SOLA VOZ POR ERROR, y la misma regla que
  // Analíticas: sin sesiones en pantalla SIEMPRE pinta una tarjeta —el estado 1
  // si no hubo señal, el estado 3 si la hubo— y la tarjeta con su botón ES el
  // mensaje, así que el toast se calla. Cierra la duplicación que el remate EH
  // dejó anotada (C-R-1 / H-R-3): dos frases distintas del mismo hecho, en
  // pantalla al mismo tiempo. Con sesiones en pantalla no pinta ninguna tarjeta
  // —el dato leído se conserva— y ahí el toast sigue siendo el único aviso.
  useEffect(() => {
    if (error && !isNetworkShapedError(error) && sessions.length > 0) {
      toast.error(t('study:log.toasts.load_failed'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  // RPW T9 (P2) — ver `tieneExamenNoFinal` arriba: un examen que sigue vivo hace
  // que esta foto caduque ya, para que la próxima mirada no la reuse.
  useEffect(() => {
    if (tieneExamenNoFinal(data)) invalidateScreenData('study-log');
  }, [data]);

  // F6.8 S-2 (pieza 3) — LA FOTO DE AYER SE CORRIGE SOLA, y esta pantalla es la
  // única que la sirve. Cuando la lectura sin señal cayó a la foto (el motor no
  // arrancó), esa vista NO fusiona entregas locales ni sella vencidos: puede
  // estar mintiendo. Al primer éxito posterior del motor, la bodega emite la
  // verdad y aquí se pinta UNA vez — sin timers, sin polling y sin re-pedir nada:
  // el dato llega HECHO en el evento. Mismo patrón de refresco por hecho
  // consumado que ya tienen el Panel y el Centro de Estudio.
  useEffect(() => {
    const onCorregida = (e) => {
      const detalle = e?.detail || {};
      if (detalle.key !== 'study-log' || detalle.data === undefined) return;
      setData(detalle.data);
    };
    window.addEventListener(SCREEN_CORRECTED_EVENT, onCorregida);
    return () => window.removeEventListener(SCREEN_CORRECTED_EVENT, onCorregida);
  }, [setData]);

  // pantallas-alumno (F), P1 de la parte 2 (HI9 y HI10): SI FALLA BORRAR, LA SESION REGRESA A SU LUGAR.
  // El borrado sigue siendo optimista (la sesion se quita al tocar y el aviso sale cuando contesta el
  // servidor). Lo nuevo: si el servidor falla, la sesion vuelve a la lista en su lugar ANTES de la recarga
  // de hoy, asi que aunque esa recarga tambien falle (HI10) la lista no miente. El lugar es el de la fecha
  // de inicio (la lista viene ordenada por `started_at`, de la mas nueva a la mas vieja).
  //
  // smoke-alumno (S10): CON EL EFECTO DE ACOMODARSE, como al borrar en el Panel (A15). Al tocar, la tarjeta baja a
  // 28 % de opacidad y se encoge a 97 % en 200 ms; despues se quita (el mismo borrado optimista de antes, ya sin
  // esperar al servidor) y las de abajo suben a su lugar en 200 ms (`useReacomodo`, el gancho del Panel). Con
  // "reducir movimiento" se quita al tocar, sin animacion. Si el servidor falla durante la despedida, la tarjeta
  // vuelve entera con la misma transicion; si falla despues, la sesion regresa a su lugar como hoy.
  const handleDelete = async (sessionId) => {
    const quitada = (sessions || []).find((s) => s.id === sessionId) || null;
    setDeleting(sessionId);
    let fallo = false;
    // Mutación del propio usuario → write-through al cache (borrado optimista,
    // igual que antes; si el DELETE falla, la sesion regresa y refetch restaura la verdad).
    const quitar = () => {
      if (fallo) return;
      setSaliendo((actual) => (actual === sessionId ? null : actual));
      setData(prev => (prev || []).filter(s => s.id !== sessionId));
    };
    let despedida = null;
    if (reducirMovimiento()) quitar();
    else {
      setSaliendo(sessionId);
      despedida = setTimeout(quitar, REACOMODO_MS);
    }
    try {
      await api.delete(`/study/sessions/${sessionId}`);
      toast.success(t('study:log.toasts.delete_success'));
    } catch {
      fallo = true;
      if (despedida !== null) clearTimeout(despedida);
      setSaliendo((actual) => (actual === sessionId ? null : actual));
      if (quitada) setData((prev) => regresarSesion(prev, quitada));
      toast.error(t('study:log.toasts.delete_failed'));
      refetch();
    } finally {
      setDeleting(null);
    }
  };

  // P2 de la parte 2: en la app sin red el bote va apagado y avisa "You need a connection".
  const boteSinRed = IS_APP_BUILD && net.isOffline;
  const alTocarSinRed = () => avisarSinRed(t);

  const encabezado = (
    <EncabezadoDePagina
      titulo={<span data-testid="study-log-title">{t('study:log.title')}</span>}
      texto={t('study:log.subtitle')}
      acciones={(
        <Boton variante="secundario" icono={ArrowLeft} onClick={() => navigate('/dashboard')} testid="back-btn">
          {t('study:log.back_btn')}
        </Boton>
      )}
    />
  );

  if (!ready) {
    return (
      <div className={PAGINA} data-testid="study-log-page">
        <EsqueletoDelHistorial />
      </div>
    );
  }

  return (
    <div className={PAGINA} data-testid="study-log-page">
      {encabezado}

      {loading ? (
        <EsqueletoDelHistorial />
      ) : sessions.length === 0 && sinRed ? (
        /* EH-1: sin señal, "aún no hay sesiones" no es un hecho de la cuenta sino
           una lectura que no salió. Con sesiones en pantalla no se toca nada: el
           dato se conserva y el chip de offline ya avisa por su cuenta.
           EH-2: el copy es el ESTADO 1, con la única sustitución autorizada
           ("tus analíticas" → "tu historial de estudio").
           R-3: y en la WEB ese copy no aplica —no hay dónde guardar nada y aquí
           sólo se llega por una petición que jamás salió—, así que ahí se pinta
           la variante neutra con su botón. La app queda sin botón (HI4).
           pantallas-alumno (F): el recuadro I2 del Panel, nube tachada. */
        <div data-testid="study-log-offline">
          {IS_APP_BUILD ? (
            <RecuadroDeEstado tipo="sin_conexion" texto={t('study:log.offline_empty')} />
          ) : (
            <RecuadroDeEstado tipo="sin_conexion" texto={t('common:offline.no_connection.body')}
              accion={{ texto: t('common:offline.no_connection.cta'), onClick: () => refetch(), icono: RotateCw, testid: 'study-log-offline-retry-btn' }} />
          )}
        </div>
      ) : sessions.length === 0 && error ? (
        /* R-1, ESTADO 3: HUBO SEÑAL Y EL SERVIDOR FALLÓ (HI5). El botón re-dispara
           LA MISMA carga de la pantalla: si sale, pinta; si no, se queda aquí. Sin
           spinner —`refetch` no levanta `loading`, así que no parpadea a
           esqueleto— y sin cadenas: un tap, una petición.
           Con sesiones en pantalla no aplica: el dato leído se conserva. */
        <div data-testid="study-log-load-error">
          <RecuadroDeEstado tipo="no_cargo" texto={t('study:log.load_error.body')}
            accion={{ texto: t('study:log.load_error.cta'), onClick: () => refetch(), icono: RotateCw, testid: 'study-log-retry-btn' }} />
        </div>
      ) : sessions.length === 0 ? (
        <div data-testid="empty-log">
          <RecuadroDeEstado tipo="vacio" icono={History} titulo={t('study:log.empty.title')} texto={t('study:log.empty.body')}
            accion={{ texto: t('study:log.empty.cta'), onClick: () => navigate('/study'), testid: 'start-studying-btn' }} />
        </div>
      ) : (
        <>
          <p className="text-[14px] leading-5 text-[#9A9A9A]" data-testid="study-log-count">{t('study:log.sessions_count', { count: sessions.length })}</p>
          {/* pantallas-alumno (F): la lista ya no vive en una caja de alto fijo; crece con la pagina (HI13). */}
          <div ref={listaRef} className="flex flex-col gap-[10px]" data-testid="session-list">
            {sessions.map((s) => (
              <CeldaQueSeDespide key={s.id} id={s.id} saliendo={saliendo === s.id}>
                <RenglonDeSesion
                  s={s}
                  t={t}
                  nombreDelModo={getLocalizedMode(t, s.mode)}
                  fecha={formatDate(s.started_at, idiomaDeFecha)}
                  borrando={deleting === s.id}
                  sinRed={boteSinRed}
                  alBorrar={handleDelete}
                  alTocarSinRed={alTocarSinRed}
                />
              </CeldaQueSeDespide>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
